import { useState, useEffect, useCallback, useRef } from 'react';
import { perLogger } from '../utils/per-logger';
import axios from "axios";

const useWebViewBridge = () => {
  const [authData, setAuthData] = useState({ jwt: `` });
  const [isMessageHandlerAttached, setIsMessageHandlerAttached] = useState(false);

  // 토큰 갱신 중인지 추적
  const [isRefreshing, setIsRefreshing] = useState(false);

  // 토큰 갱신 후 재시도할 요청들을 저장하는 큐
  const requestQueue = useRef([]);

  // API 요청을 위한 함수 (401 응답 시 토큰 갱신 후 재요청)
  const fetchWithTokenRefresh = useCallback(async (
      url,
      options = {},
      config = {}
  ) => {
    // JWT 토큰이 없으면 바로 에러 반환
    if (!authData.jwt) {
      throw new Error('인증 토큰이 없습니다');
    }

    try {
      // 헤더에 인증 토큰 추가
      const headers = {
        ...config.headers,
        'Authorization': `Bearer ${authData.jwt}`
      };

      // API 요청
      // const response = await fetch(url, {
      //   ...options,
      //   headers
      // });

      const response = await axios.post(
          url,
          {...options},
          { headers, timeout : config.timeout }
      );

      // 401 Unauthorized 에러 처리
      if (response.status === 401) {
        perLogger.log('토큰 만료 감지 (401 응답)', {ignore: ['prod'], consoleMode: true});

        // 현재 요청 정보 저장
        const retryRequest = { url, options };

        // 토큰 갱신 및 재요청
        return await handleTokenRefreshAndRetry(retryRequest);
      }

      return response.data;

    } catch (error) {
      perLogger.log(`API 요청 오류: ${error.message}`, {ignore: ['prod'], consoleMode: true});
      throw error;
    }
  }, [authData.jwt]);

  // 토큰 갱신 및 요청 재시도 처리
  const handleTokenRefreshAndRetry = useCallback(async (retryRequest) => {
    // 이미 토큰 갱신 중이면 큐에 추가하고 갱신 완료 대기
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        requestQueue.current.push({
          request: retryRequest,
          resolve,
          reject
        });
      });
    }

    try {
      setIsRefreshing(true);

      // 현재 요청을 큐에 추가
      const requestPromise = new Promise((resolve, reject) => {
        requestQueue.current.push({
          request: retryRequest,
          resolve,
          reject
        });
      });

      // 토큰 갱신 요청
      await requestTokenRefresh();

      return requestPromise;
    } catch (error) {
      perLogger.log(`토큰 갱신 실패: ${error.message}`, {ignore: ['prod'], consoleMode: true});
      throw error;
    }
  }, [isRefreshing]);

  // 큐에 있는 요청들 처리
  const processRequestQueue = useCallback(async (newToken) => {
    const queue = [...requestQueue.current];
    requestQueue.current = [];

    for (const { request, resolve, reject } of queue) {
      try {
        // 새 토큰으로 헤더 업데이트
        const headers = {
          ...request.options.headers,
          'Authorization': `Bearer ${newToken}`
        };

        // 요청 재시도
        const response = await fetch(request.url, {
          ...request.options,
          headers
        });

        resolve(response);
      } catch (error) {
        reject(error);
      }
    }
  }, []);

  // 토큰 갱신 요청 함수
  const requestTokenRefresh = useCallback(() => {
    console.log("토큰 refresh 요청");

    return new Promise((resolve, reject) => {
      // 토큰 갱신 타임아웃 설정
      const timeoutId = setTimeout(() => {
        setIsRefreshing(false);
        reject(new Error('토큰 갱신 요청 타임아웃'));
      }, 10000); // 10초 타임아웃

      // 토큰 갱신 응답을 기다릴 리스너
      const tokenRefreshListener = (event) => {
        const data = event.data;

        if (data && data.type === 'TOKEN_REFRESH_RESPONSE') {
          clearTimeout(timeoutId);
          window.removeEventListener('message', tokenRefreshListener);

          if (data.payload.success) {
            // 새 토큰으로 상태 업데이트
            setAuthData({ jwt: data.payload.jwt });
            setIsRefreshing(false);

            // 큐에 있는 요청들 처리
            processRequestQueue(data.payload.jwt);
            resolve(data.payload.jwt);
          } else {
            setIsRefreshing(false);
            reject(new Error('토큰 갱신 실패'));
          }
        }
      };

      // 리스너 등록
      window.addEventListener('message', tokenRefreshListener);

      // 앱으로 토큰 갱신 요청 메시지 전송
      sendMessage('TOKEN_REFRESH_REQUEST', {});
      perLogger.log('앱에 토큰 갱신 요청', {ignore: ['prod'], consoleMode: true});
    });
  }, [authData.jwt, processRequestQueue]);

  // 앱으로 커스텀 메시지 전송 메서드
  const sendMessage = useCallback((type, payload) => {
    const message = JSON.stringify({
      type,
      payload
    });

    window.Flutter?.postMessage(message);
    perLogger.log(`앱으로 메시지 전송: ${message}`, {ignore: ['prod'], consoleMode: true});
  }, []);

  // 메시지 핸들러 설정
  useEffect(() => {
    if (isMessageHandlerAttached) return;

    function handleMessage(event) {
      const data = event.data;
      perLogger.log(`수신된 메시지: ${JSON.stringify(data)}`, {ignore: ['prod'], consoleMode: true});

      // 인증 데이터면 authData 갱신
      if (data && data.type === 'AUTHORIZATION_RESPONSE') {
        setAuthData({ jwt: data.payload.jwt });
      }
    }

    window.addEventListener('message', handleMessage);
    setIsMessageHandlerAttached(true);

    return () => {
      window.removeEventListener('message', handleMessage);
      setIsMessageHandlerAttached(false);
    };
  }, []);

  // URL 쿼리 파라미터 파싱 유틸리티
  const getQueryParam = useCallback((paramName) => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get(paramName);
  }, []);

  return {
    authData,
    isAuthenticated: Boolean(authData.jwt),
    sendMessage,
    requestTokenRefresh,
    fetchWithTokenRefresh,
    getQueryParam
  };
};

export default useWebViewBridge;

// 🔐 인증 데이터 요청
// {
//   "type": "AUTHORIZATION_REQUEST",
//   "payload": {}
// }
// 🔐 인증 데이터 응답
// {
//   "type": "AUTHORIZATION_RESPONSE",
//   "payload": {
//     "success": true,
//     "jwt": "jwt",
//   }
// }
//
// ✅ 토큰 갱신 요청
// {
//   "type": "TOKEN_REFRESH_REQUEST",
//   "payload": {}
// }
// ✅ 토큰 갱신 응답
// {
//   "type": "TOKEN_REFRESH_RESPONSE",
//   "payload": {
//     "success": true,
//     "jwt": "jwt",
//   }
// }
